/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

// EVALUATE
import { CalendarDate } from '@internationalized/date';
import forEach from 'lodash/forEach';
import lodashGet from 'lodash/get';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import toInteger from 'lodash/toInteger';
import toNumber from 'lodash/toNumber';
import toString from 'lodash/toString';
import lodashValues from 'lodash/values';
import { formatMoney, localToday, toTitleCase } from 'sarsaparilla';
import { PASS_LABEL_MAP } from 'site-kit';

import { Campground, Campsite, PriceDetails, Reservation } from '../../../types/camping';
import { CAMPING_BASEPATH } from '../constants';
import { ITINERARY_SINGLE_TYPE_FACILITY } from '../constants/campground';
import { DATE_FORMAT_NUMERIC } from '../constants/dateFormats';
import {
    GOLDEN_ACCESS_PASS,
    GOLDEN_AGE_PASS,
    INTERAGENCY_ACCESS_PASS,
    INTERAGENCY_ANNUAL_SENIOR_PASS,
    INTERAGENCY_LIFETIME_SENIOR_PASS,
    INTERAGENCY_PASSES,
} from '../constants/passes';
import { PENDING_MODIFICATION } from '../constants/reservation';
import { UTCStringToCalendarDate, format } from './dateUtils';
import { isMultiSiteReservation } from './reservation_utils';
import { formatStateAbbreviations } from './string_utils';

export const navigateToOrderDetailsUrl = (reservationIds: string[]) => {
    let param = '';
    forEach(reservationIds, (id) => {
        param += param === '' ? '?' : '&';
        param += `id=${id}`;
    });

    return `${CAMPING_BASEPATH}/reservations/orderdetails${param}`;
};

export const navigateToRentalOrderDetailsUrl = (reservationIds: string[]) => {
    let param = '';
    forEach(reservationIds, (id) => {
        param += param === '' ? '?' : '&';
        param += `rid=${id}`;
    });

    return `${CAMPING_BASEPATH}/reservations/orderdetails${param}`;
};

export const navigateToCombineOrderDetailsUrl = (
    reservationIds: { id: string; type: string }[]
) => {
    let param = '';
    forEach(reservationIds, (id) => {
        const urlId = isEqual(id.type, 'campsite') ? 'id' : 'rid';
        param += param === '' ? '?' : '&';
        param += `${urlId}=${id.id}`;
    });
    return `${CAMPING_BASEPATH}/reservations/orderdetails${param}`;
};

export const hasExceededMaxMod = (maxChanges: number, statusHistory: string[]) => {
    // count how many 'PENDING_MODIFICATION' are there in reservation status history
    const numOfChanges = reduce(
        statusHistory,
        (result, status) => {
            let newCount = result;
            if (status === PENDING_MODIFICATION) {
                newCount += 1;
            }
            return newCount;
        },
        0
    );

    // if number of changes is equal or higher (likely ==) than the max change facility rule, reservation can no longer be modified
    return numOfChanges >= maxChanges;
};

export const areAllFeesWaived = (reservation?: Reservation | null): boolean =>
    lodashGet(reservation, 'flags.WAIVE_ALL_FEES', false);

export const applyAllFeesWaived = (
    priceDetails: PriceDetails,
    reservation?: Reservation | null
): PriceDetails => {
    if (!areAllFeesWaived(reservation)) {
        return priceDetails;
    }

    return {
        ...priceDetails,
        line_items: [],
        subtotal: 0,
        total_cost: 0,
        total_discount: 0,
        total_tax: 0,
        pending_balance: 0,
    };
};

export const EQUIPMENT_DETAILS_KEY = 'equipment_details';
export const SITE_DETAILS_KEY = 'site_details';

/**
 * calculate the num_occupant value from the current group size info
 * @param {object} groupSize - the full group size request object
 * @param {number} campsiteId - the campsite id of the group size to be changed (optional)
 */
export const getNumOccupants = (groupSize: any, campsiteId: string) => {
    if (!groupSize) {
        return 0;
    }
    let largestGroupSize = 0;
    // if campsite id is defined, return the corresponding campsite group size value (catered to ITINERARY res)
    if (campsiteId) {
        largestGroupSize = toInteger(lodashGet(groupSize, `group_size-${campsiteId}`, 0));
    }
    // if campsite id is NOT defined, find the max group size value and return (catered to ITINERARY_single, STANDARD and RENTAL res)
    else {
        forEach(groupSize, (value, key) => {
            if (
                key !== 'is_group_size_same' &&
                key !== 'group_size' &&
                toInteger(value) > largestGroupSize
            ) {
                largestGroupSize = toInteger(value);
            }
        });
    }

    // if is_group_size_same is true, return the value of 'group_size', otherwise, calculate further
    const numOccupants = isEqual(groupSize.is_group_size_same, 'true')
        ? toInteger(lodashGet(groupSize, 'group_size', 0))
        : largestGroupSize;

    return numOccupants;
};

/* =============================
            PASS
============================== */
export const constructPassTypeOptions = (passTypeOptions: string[]) => {
    const passTypes = [
        {
            label: 'Select only from these eligible discount passes:',
            value: '',
        },
    ];

    forEach(passTypeOptions, (value) => {
        if (!PASS_LABEL_MAP[value]) {
            return;
        }
        passTypes.push({ label: PASS_LABEL_MAP[value], value });
    });

    return passTypes;
};

/**
 * @param {string} value - pass number
 */
export const isPassNumberValidOld = (type: string, value: string) => {
    const year = value.substring(0, 2);

    // if pass is before 2007 or after 2021, return false
    if (toNumber(year) < 7 || toNumber(year) > 21) {
        return false;
    }

    // if pass is INTERAGENCY_ACCESS_PASS, 3rd digit must be '3'
    if (type === INTERAGENCY_ACCESS_PASS) {
        const doesPassMatchRegex = toString(value).match(/[0-9]{2}3[0-9]{6}$/);
        return !isNil(doesPassMatchRegex);
    }
    // if pass is INTERAGENCY_ANNUAL_SENIOR_PASS, 3rd digit must be '6'
    if (type === INTERAGENCY_ANNUAL_SENIOR_PASS) {
        const doesPassMatchRegex = toString(value).match(/[0-9]{2}6[0-9]{6}$/);
        return !isNil(doesPassMatchRegex);
    }
    // if pass is INTERAGENCY_LIFETIME_SENIOR_PASS, 3rd digit must be '2'
    const doesPassMatchRegex = toString(value).match(/[0-9]{2}2[0-9]{6}$/);
    return !isNil(doesPassMatchRegex);
};

const isDigitalIAPPassNumberValid = (value: string) => {
    const doesPassMatchRegex = toString(value).match(/#?\d{10}-\d/);
    return !isNil(doesPassMatchRegex);
};

const goldenPassValidation = (value: string) => {
    const doesPassMatchRegex = toString(value).match(/[0-9]{2}[0-9-][0-9]{2,8}$/);
    return (
        value.length >= 5 &&
        value.length <= 11 &&
        !isNil(doesPassMatchRegex) &&
        doesPassMatchRegex[0] === value
    );
};

const interagencyPassValidation = (type: string, value: string) => {
    const year = value.substring(0, 2);
    const passFormat = /[0-9]{2}[BCFbcf][A-Za-z][0-9]{5}$/;
    const doesPassMatchRegex = toString(value).match(passFormat);
    // pass is VALID if it is issued 2021 onward, and matches the regex format
    const isNewValidationValid = toNumber(year) >= 21 && !isNil(doesPassMatchRegex);

    // we need to perform both validations for interagency passes, if any succeeds we consider the pass valid
    return isNewValidationValid || isPassNumberValidOld(type, value);
};

export const isPhysicalPassNumberValid = (type: string, value: string) => {
    // (1) if pass number is empty, not valid
    if (isEmpty(value)) {
        return false;
    }

    // (2) if pass is golden access or golden age
    if (type === GOLDEN_ACCESS_PASS || type === GOLDEN_AGE_PASS) {
        return goldenPassValidation(value);
    }

    // (3) if interagency pass, validate against regex requirement
    const isInteragencyPass = INTERAGENCY_PASSES.includes(type);
    if (isInteragencyPass) {
        const year = value.substring(0, 2);

        if (type === INTERAGENCY_ANNUAL_SENIOR_PASS) {
            const currentYear = localToday.year % 100;
            const lastYear = currentYear - 1;

            if (toNumber(year) < lastYear || toNumber(year) > currentYear) {
                return false;
            }
            return interagencyPassValidation(type, value);
        }
        if (
            type === INTERAGENCY_ACCESS_PASS ||
            type === INTERAGENCY_LIFETIME_SENIOR_PASS
        ) {
            return interagencyPassValidation(type, value) || goldenPassValidation(value);
        }
    }

    return true;
};

/**
 * @param {string} type - pass type
 * @param {string} value - pass number
 */
export const isPassNumberValid = (type: string, value: string) => {
    return isPhysicalPassNumberValid(type, value) || isDigitalIAPPassNumberValid(value);
};

export const transformOrderItems = (
    reservation: Reservation,
    campground: Campground,
    campsites: Record<string, Campsite>
) => {
    const facilityType = lodashGet(campground, 'facility_type', '');
    const { night_map: nightMap } = reservation;
    const hasMultipleSites = isMultiSiteReservation(nightMap);

    /** If the facility is ITINERARY_SINGLE */
    if (
        (facilityType === ITINERARY_SINGLE_TYPE_FACILITY || hasMultipleSites) &&
        reservation.night_map
    ) {
        const nightMapKeys = Object.keys(nightMap);

        // nightMap may not be in order, so we sort it
        nightMapKeys.sort((a, b) => {
            return new Date(a).getTime() - new Date(b).getTime();
        });

        // Convert nightMap into an actual itinerary
        const itinerary: Array<{
            campsiteId: string;
            campsiteName: string;
            campsiteLoop: string;
            startDate: CalendarDate;
            endDate: CalendarDate;
        }> = [];

        nightMapKeys.forEach((night) => {
            const campsiteId = nightMap[night].campsite_id;
            const campsiteName = nightMap[night].campsite_name;
            const campsiteLoop = nightMap[night].campsite_loop;
            const nightDate = UTCStringToCalendarDate(night);
            const nightAfterDate = nightDate.add({ days: 1 });
            const lastItineraryItem = itinerary[itinerary.length - 1];

            // If previous night was at the same campsite, update previous
            // itinerary item's end date
            if (lastItineraryItem && lastItineraryItem.campsiteId === campsiteId) {
                lastItineraryItem.endDate = nightAfterDate;
            } else {
                // is new campsite; push itinerary item to array
                itinerary.push({
                    campsiteId,
                    campsiteName: campsiteName || (campsites[campsiteId]?.name ?? ''),
                    campsiteLoop: campsiteLoop || (campsites[campsiteId]?.loop ?? ''),
                    startDate: nightDate,
                    endDate: nightAfterDate,
                });
            }
        });

        // return the itinerary in the format needed to display
        return map(itinerary, (item) => {
            const matchedCampsite = campsites[item.campsiteId];
            const typeOfUse = lodashGet(matchedCampsite, 'type_of_use', 'Overnight');
            return {
                orderItemTitle: `Site: ${item.campsiteName}, Loop: ${item.campsiteLoop}`,
                orderItemDetailTwo:
                    !isNil(typeOfUse) && includes(typeOfUse, 'Day')
                        ? `Reserved for: ${format(item.startDate, DATE_FORMAT_NUMERIC)}`
                        : `Reserved for: ${format(item.startDate, DATE_FORMAT_NUMERIC)} - ${format(item.endDate, DATE_FORMAT_NUMERIC)}`,
                orderItemPrice: `${formatMoney(reservation.total_cost)}`,
            };
        });
    }

    const campsite = lodashValues(campsites)[0];
    const typeOfUse = lodashGet(campsite, 'type_of_use', 'Overnight');
    /** Not ITINERARY_SINGLE */
    return [
        {
            orderItemTitle: `Site ${lodashGet(
                campsite,
                'name',
                lodashGet(campsite, 'campsite_name', '')
            )}, ${lodashGet(campsite, 'loop', '')}`,
            orderItemDetailOne: `${formatStateAbbreviations(
                toTitleCase(campground.facility_name)
            )}`,
            orderItemDetailTwo:
                !isNil(typeOfUse) && includes(typeOfUse, 'Day')
                    ? `Reserved for: ${format(UTCStringToCalendarDate(reservation.start_date), DATE_FORMAT_NUMERIC)}`
                    : `Reserved for: ${format(UTCStringToCalendarDate(reservation.start_date), DATE_FORMAT_NUMERIC)} - ${format(UTCStringToCalendarDate(reservation.end_date), DATE_FORMAT_NUMERIC)}`,
            orderItemPrice: `${formatMoney(reservation.total_cost)}`,
        },
    ];
};

export const getSupplementalEventsProps = (
    hasSupplementalInventory: boolean,
    hasSupplementalInventorySelected: boolean
) => {
    let clickTagAction = 'add-on-reservation';

    if (!hasSupplementalInventory && !hasSupplementalInventorySelected) {
        return {};
    }

    if (hasSupplementalInventory && hasSupplementalInventorySelected) {
        clickTagAction = 'add-on-reservation-added';
    }

    return {
        clickTagCategory: 'Camping',
        clickTagAction,
        clickTagLabel: 'add-on event',
    };
};
